const PRODUCTION_BASE_URL = 'https://dev-agent.savictech.com/sf';

function resolveBaseUrl() {
  const envUrl = (process.env.REACT_APP_FORECASTING_API_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/$/, '');
  return PRODUCTION_BASE_URL;
}

const BASE_URL = resolveBaseUrl();

// ─── Token storage ────────────────────────────────────────────────────────────

const SF_TOKEN_KEY   = 'sf_access_token';
const SF_REFRESH_KEY = 'sf_refresh_token';

export const getToken        = () => localStorage.getItem(SF_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(SF_REFRESH_KEY);

export const setTokens = ({ access_token, refresh_token }) => {
  if (access_token)  localStorage.setItem(SF_TOKEN_KEY,   access_token);
  if (refresh_token) localStorage.setItem(SF_REFRESH_KEY, refresh_token);
};

export const clearTokens = () => {
  localStorage.removeItem(SF_TOKEN_KEY);
  localStorage.removeItem(SF_REFRESH_KEY);
};

// Callback invoked when a 401 can't be recovered — lets the app redirect to login
let _sessionExpiredCb = null;
export const onSessionExpired = (cb) => { _sessionExpiredCb = cb; };

// ─── Internal helpers ─────────────────────────────────────────────────────────

async function tryRefresh() {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    const res = await fetch(`${BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setTokens({ access_token: data.access_token });
    return true;
  } catch {
    return false;
  }
}

async function apiFetch(path, options = {}) {
  const token = getToken();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const headers = { ...(options.headers || {}), ...authHeader };

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  } catch (err) {
    throw new Error(
      `Cannot connect to Sales Forecasting backend at ${BASE_URL}. ` +
      'Set REACT_APP_FORECASTING_API_URL to your API server (e.g. http://localhost:8000).'
    );
  }

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      const newHeaders = { ...headers, Authorization: `Bearer ${getToken()}` };
      try {
        res = await fetch(`${BASE_URL}${path}`, { ...options, headers: newHeaders });
      } catch (err) {
        throw new Error('Cannot connect to Sales Forecasting backend.');
      }
    } else {
      clearTokens();
      if (_sessionExpiredCb) _sessionExpiredCb();
      throw new Error('Session expired. Please log in again.');
    }
  }

  return res;
}

async function handleResponse(res) {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (Array.isArray(data.detail)) {
        detail = data.detail
          .map((e) => `[${Array.isArray(e.loc) ? e.loc.join(' -> ') : e.loc}] ${e.msg}`)
          .join(' | ');
      } else if (typeof data.detail === 'string') {
        detail = data.detail;
      } else {
        detail = JSON.stringify(data);
      }
    } catch (_) {}
    throw new Error(detail);
  }
  if (res.status === 204) return null;
  return res.json();
}

function buildQs(params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null)
  );
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
}

// ─── AUTH ─────────────────────────────────────────────────────────────────────

export const sfLogin = (payload) =>
  fetch(`${BASE_URL}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const sfLogout = () =>
  apiFetch('/api/v1/auth/logout', { method: 'POST' })
    .then(res => res.status === 204 ? null : res.json().catch(() => null));

export const sfRefresh = (refresh_token) =>
  fetch(`${BASE_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  }).then(handleResponse);

export const sfGetMe = () =>
  apiFetch('/api/v1/auth/me').then(handleResponse);

// ─── FORECASTS ───────────────────────────────────────────────────────────────

export const generateForecast = (payload) =>
  apiFetch('/api/v1/forecasts/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const getForecastRun = (runId) =>
  apiFetch(`/api/v1/forecasts/runs/${encodeURIComponent(runId)}`).then(handleResponse);

export const getForecast = (forecastId) =>
  apiFetch(`/api/v1/forecasts/${encodeURIComponent(forecastId)}`).then(handleResponse);

export const listForecasts = (params = {}) =>
  apiFetch(`/api/v1/forecasts/${buildQs(params)}`).then(handleResponse);

// ─── QUOTAS ──────────────────────────────────────────────────────────────────

export const createQuota = (payload) =>
  apiFetch('/api/v1/quotas/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const listQuotas = (params = {}) =>
  apiFetch(`/api/v1/quotas/${buildQs(params)}`).then(handleResponse);

export const getQuota = (quotaId) =>
  apiFetch(`/api/v1/quotas/${encodeURIComponent(quotaId)}`).then(handleResponse);

export const updateQuota = (quotaId, payload) =>
  apiFetch(`/api/v1/quotas/${encodeURIComponent(quotaId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

// ─── QUOTA ADJUSTMENTS ───────────────────────────────────────────────────────

export const createAdjustment = (payload) =>
  apiFetch('/api/v1/quotas/adjustments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const adjustQuota = (quotaId, payload) =>
  apiFetch(`/api/v1/quotas/${encodeURIComponent(quotaId)}/adjust`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const approveQuota = (quotaId, payload) =>
  apiFetch(`/api/v1/quotas/${encodeURIComponent(quotaId)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const rejectQuota = (quotaId, payload) =>
  apiFetch(`/api/v1/quotas/${encodeURIComponent(quotaId)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

export const sapSync = (quotaId) =>
  apiFetch(`/api/v1/quotas/${encodeURIComponent(quotaId)}/sap-sync`, {
    method: 'POST',
  }).then(handleResponse);

export const getQuotaEntities = () =>
  apiFetch('/api/v1/quotas/entities').then(handleResponse);

// ─── WORKFLOWS ───────────────────────────────────────────────────────────────

export const listWorkflows = (params = {}) =>
  apiFetch(`/api/v1/workflows/${buildQs(params)}`).then(handleResponse);

export const getWorkflow = (workflowId) =>
  apiFetch(`/api/v1/workflows/${encodeURIComponent(workflowId)}`).then(handleResponse);

export const approveWorkflow = (workflowId, payload) =>
  apiFetch(`/api/v1/workflows/${encodeURIComponent(workflowId)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

// ─── PERFORMANCE ─────────────────────────────────────────────────────────────

export const listPerformance = (params = {}) =>
  apiFetch(`/api/v1/performance/${buildQs(params)}`).then(handleResponse);

export const getPerformanceSummary = (entityType, entityId, params = {}) =>
  apiFetch(
    `/api/v1/performance/summary/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${buildQs(params)}`
  ).then(handleResponse);

export const getPerformanceExceptions = (params = {}) =>
  apiFetch(`/api/v1/performance/exceptions${buildQs(params)}`).then(handleResponse);

export const triggerAlerts = (params = {}) =>
  apiFetch(`/api/v1/performance/alerts/trigger${buildQs(params)}`, {
    method: 'POST',
  }).then(handleResponse);

// ─── AUDIT LOG ───────────────────────────────────────────────────────────────

export const listAuditLogs = (params = {}) =>
  apiFetch(`/api/v1/audit/${buildQs(params)}`).then(handleResponse);

export const getAuditLog = (logId) =>
  apiFetch(`/api/v1/audit/${encodeURIComponent(logId)}`).then(handleResponse);

export const exportAuditCsv = async (params = {}) => {
  const res = await apiFetch(`/api/v1/audit/export/csv${buildQs(params)}`);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { const d = await res.json(); detail = d.detail || detail; } catch (_) {}
    throw new Error(detail);
  }
  return res.blob();
};

// ─── CHAT ─────────────────────────────────────────────────────────────────────

export const sendChat = (payload) =>
  apiFetch('/api/v1/chat/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(handleResponse);

// ─── SYSTEM ──────────────────────────────────────────────────────────────────

export const healthCheck = () =>
  apiFetch('/health').then(handleResponse);

export { BASE_URL as FORECASTING_API_BASE_URL };
